import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Markdown } from '../components/Markdown';
import { Spinner } from '../components/ui';

interface Subcategory { id: string; fn: string; category: string; description: string; topics: string | null }
interface SubDetail extends Subcategory { about: string | null; actions: string | null; documentation: string | null; refs: string | null; actors: string | null }

const FNS = ['', 'Govern', 'Map', 'Measure', 'Manage'];

export function NistBrowser() {
  const [subs, setSubs] = useState<Subcategory[] | null>(null);
  const [fn, setFn] = useState('');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState<SubDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  async function load() {
    const qs = new URLSearchParams();
    if (fn) qs.set('fn', fn);
    if (q) qs.set('q', q);
    const r = await api.get<{ subcategories: Subcategory[] }>(`/api/nist?${qs.toString()}`);
    setSubs(r.subcategories);
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [fn]);

  async function openDetail(id: string) {
    setLoadingDetail(true);
    try {
      const r = await api.get<{ subcategory: SubDetail }>(`/api/nist/detail/${encodeURIComponent(id)}`);
      setDetail(r.subcategory);
    } finally {
      setLoadingDetail(false);
    }
  }

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-xl font-bold text-slate-50">NIST AI RMF Playbook Library</h1>
        <p className="mt-1 text-sm text-slate-400">
          All 72 subcategories across Govern · Map · Measure · Manage (NIST AI 100-1). Voluntary and
          context-driven — map what fits each client from an engagement's NIST tab.
        </p>
      </div>
      <div className="mb-4 flex gap-2">
        <select className="input !w-36" value={fn} onChange={(e) => setFn(e.target.value)}>
          {FNS.map((f) => <option key={f} value={f}>{f || 'All functions'}</option>)}
        </select>
        <input
          className="input max-w-md flex-1"
          placeholder="Search id, description, topics, about…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void load(); }}
        />
        <button className="btn-secondary" onClick={() => void load()}>Search</button>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="max-h-[75vh] space-y-2 overflow-y-auto pr-1">
          {subs === null ? (
            <Spinner />
          ) : (
            subs.map((s) => (
              <button
                key={s.id}
                onClick={() => void openDetail(s.id)}
                className={`card block w-full !p-3 text-left transition-colors hover:border-brand-600 ${detail?.id === s.id ? 'border-brand-600' : ''}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-bold text-brand-300">{s.id}</span>
                  <span className="chip border-slate-700 text-slate-400">{s.fn}</span>
                </div>
                <p className="mt-1 text-xs text-slate-400">{s.description}</p>
              </button>
            ))
          )}
        </div>
        <div className="card max-h-[75vh] overflow-y-auto">
          {loadingDetail ? (
            <Spinner />
          ) : detail ? (
            <>
              <h2 className="text-lg font-bold text-brand-300">{detail.id}</h2>
              <p className="mt-1 text-sm font-semibold text-slate-200">{detail.description}</p>
              {detail.topics && <p className="mt-1 text-xs text-slate-500">Topics: {detail.topics}</p>}
              {detail.actors && <p className="text-xs text-slate-500">AI actors: {detail.actors}</p>}
              {detail.about && (<><h3 className="mt-4 text-sm font-bold text-slate-100">About</h3><Markdown content={detail.about} /></>)}
              {detail.actions && (<><h3 className="mt-4 text-sm font-bold text-slate-100">Suggested actions</h3><Markdown content={detail.actions} /></>)}
              {detail.documentation && (<><h3 className="mt-4 text-sm font-bold text-slate-100">Documentation prompts</h3><Markdown content={detail.documentation} /></>)}
              {detail.refs && (<><h3 className="mt-4 text-sm font-bold text-slate-100">References</h3><Markdown content={detail.refs} /></>)}
            </>
          ) : (
            <p className="py-16 text-center text-sm text-slate-500">Select a subcategory to read its playbook entry.</p>
          )}
        </div>
      </div>
    </div>
  );
}
